import { IRLinkAPI } from "../types";

/**
 * @Doc Implementation for the `RLinkAPI` interface operating in mode `auto`
 *
 * This class provides an implementation of the `IRLinkAPI` with the following behavior:
 * - Automatically triggers rendering unless explicitly prevented.
 * - Exposes methods for controlling rendering behavior via `getAPIPayload`.
 *
 * API Usage Example:
 * ```html
 * <!-- Include the Readership Link script in your HTML -->
 * <script src="https://cdn.btmessage.com/rlink.js"></script>
 * <script>
 * // Listen to the event indicating the Readership Link script was loaded and the render criteria were met.
 * // Use { once: true } to ensure the listener is automatically removed after first trigger.
 * // This event is dispatched multiple times to avoid race conditions.
 * // Important: It needs to be added before the Readership Link script is loaded.
 * window.addEventListener('bt-rlink-api-ready', function(event) {
 *     // After the step above, the API can be called anytime
 *     const api = event.detail.api();
 *     // Prevent automatic rendering
 *     api.prevent();
 * }, { once: true });
 * </script>
 * ```
 * */
export class AutoModeAPI implements IRLinkAPI {
  private shouldRender = true;
  /**
   * @param render - The callback function to invoke when rendering.
   * @param apiPreventWaitTime - The wait time (in milliseconds) before auto-rendering is triggered.
   */
  constructor(
    private render: () => void,
    private apiPreventWaitTime: number,
  ) {}

  start() {
    this.renderIfNotPrevented();
  }

  /**
   * Retrieves the payload containing methods for controlling rendering behavior.
   *
   * The payload includes:
   * - `prevent`: Prevents automatic rendering.
   * - `render`: Manually triggers the rendering callback.
   *
   * @returns RLinkAPIAutoModePayload - The API payload for controlling rendering.
   */
  getAPIPayload() {
    return {
      prevent: () => {
        this.preventRender();
      },
      render: () => {
        this.render();
      },
    };
  }

  /**
   * Handles automatic rendering after the specified wait time unless prevented.
   */
  private renderIfNotPrevented() {
    setTimeout(() => {
      if (!this.shouldRender) return;
      this.render();
    }, this.apiPreventWaitTime);
  }

  /**
   * Prevents automatic rendering.
   */
  private preventRender() {
    this.shouldRender = false;
  }
}
